import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.js';
import { errorMessage } from '../api/client.js';
import { SSO_ERROR_KEY } from '../auth/ssoBridge.js';

/** Read + clear a one-shot SSO rejection reason (e.g. "no CRM account") stashed by the
 *  auth bootstrap, so a failed SSO sign-in lands here with an explanation, shown once. */
function takeSsoError(): string | null {
  try {
    const msg = sessionStorage.getItem(SSO_ERROR_KEY);
    if (msg) sessionStorage.removeItem(SSO_ERROR_KEY);
    return msg;
  } catch {
    return null;
  }
}

export function LoginPage() {
  const { login, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get('redirect') || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(() => takeSsoError());

  // Already logged in → bounce to target (declarative, safe during render).
  if (user) {
    return <Navigate to={redirect} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__brand">
          <span className="sidebar__logo">◆</span>
          <span>{t('login.brand')}</span>
        </div>
        <h1>{t('login.title')}</h1>
        <p className="muted">{t('login.subtitle')}</p>

        <form onSubmit={handleSubmit} className="login-form">
          <label className="field">
            <span className="field__label">{t('login.emailLabel')}</span>
            <input
              className="field__input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </label>
          <label className="field">
            <span className="field__label">{t('login.passwordLabel')}</span>
            <input
              className="field__input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>

          {error && <div className="form-error" role="alert">{error}</div>}

          <button
            type="submit"
            className="btn btn--primary btn--block"
            disabled={submitting || !email || !password}
          >
            {submitting ? t('login.submitting') : t('login.submit')}
          </button>
        </form>

        <p className="muted" style={{ marginTop: 16, textAlign: 'center' }}>
          <Link to="/forgot-password">{t('login.forgotPassword')}</Link>
        </p>
      </div>
    </div>
  );
}
